import _ from 'lodash';
import { getControllerApiVersion } from './utils.js';
import { restoreSettingsDraft } from './settings_draft';
import { supportsRootSenseFirmwareVersion } from './system_settings_utils';

const IRRIGATION_TIME_STRATEGIES = [2, 3];
const IRRIGATION_AUTO_STRATEGIES = [0, 4];
const IRRIGATION_VWC_STRATEGIES = [1, 5];
const INDUSTRIAL_LITE_CONTROLLER_TYPE = 5155;
const INDUSTRIAL_LITE_MODERN_IRRIGATION_API_MIN = 'BAD';
const INDUSTRIAL_LITE_EXTENDED_IRRIGATION_API_MIN = 'BAE';
const GROWBASE_PRO_CONTROLLER_TYPE = 5055;
const GROWBASE_PRO_ROOT_ZONE_COUNT_API_MIN = 'BAF';
const ROOTSENSE_ADDRESS_SETUP_API_MIN = 'BAICA';

export const P1_MINIMUM_TOTAL_SHOT_DURATION_MINUTES =
  'irrigation_p1_minimum_total_shot_duration_minutes';

export function isIrrigationTimeStrategy(strategy) {
  return IRRIGATION_TIME_STRATEGIES.includes(Number(strategy));
}

export function isIrrigationAutoStrategy(strategy) {
  return IRRIGATION_AUTO_STRATEGIES.includes(Number(strategy));
}

export function isIrrigationAutoSimpleStrategy(strategy) {
  return Number(strategy) === 4;
}

export function isIrrigationVwcStrategy(strategy) {
  return IRRIGATION_VWC_STRATEGIES.includes(Number(strategy));
}

export function isIrrigationVwcSimpleStrategy(strategy) {
  return Number(strategy) === 5;
}

export function isIrrigationAdvancedVwcStrategy(strategy) {
  return [0, 1].includes(Number(strategy));
}

function supportsIndustrialLiteApi(controller, minimumApiVersion) {
  return (
    Number(controller?.type) !== INDUSTRIAL_LITE_CONTROLLER_TYPE ||
    getControllerApiVersion(controller).localeCompare(minimumApiVersion) >= 0
  );
}

export function supportsModernIrrigationStrategies(controller) {
  return supportsIndustrialLiteApi(controller, INDUSTRIAL_LITE_MODERN_IRRIGATION_API_MIN);
}

export function supportsP1MinimumCumulativeIrrigationTime(controller) {
  return supportsIndustrialLiteApi(controller, INDUSTRIAL_LITE_EXTENDED_IRRIGATION_API_MIN);
}

export function supportsIrrigationTankInfo(controller) {
  return supportsIndustrialLiteApi(controller, INDUSTRIAL_LITE_EXTENDED_IRRIGATION_API_MIN);
}

export function supportsGrowBaseProRootZoneCount(controller) {
  return (
    Number(controller?.type) !== GROWBASE_PRO_CONTROLLER_TYPE ||
    getControllerApiVersion(controller).localeCompare(GROWBASE_PRO_ROOT_ZONE_COUNT_API_MIN) >= 0
  );
}

export function supportsRootZoneAddressSetup(controller) {
  return Number(controller?.type) === GROWBASE_PRO_CONTROLLER_TYPE &&
    getControllerApiVersion(controller).localeCompare(ROOTSENSE_ADDRESS_SETUP_API_MIN) >= 0;
}

export function getRootZoneAddressUnavailableReason(controller) {
  // The shared BAICA contract includes extension firmware reporting.
  if (!supportsRootSenseFirmwareVersion(controller)) return null;
  const system = controller?.settings?.system;
  if ((system?.root_sense_link_fw_flags & 3) !== 3) return 'gc_ms_root_addr_unavailable';
  if (system.root_sense_link_fw_major > 0 || system.root_sense_link_fw_minor > 0 ||
      system.root_sense_link_fw_patch >= 15) return null;
  return 'gc_ms_root_addr_update_required';
}

export function getRootZoneIrrigationShowCounts(controller, values, isSimulation = false) {
  const irrigation = Number(values?.irrigation_show_cnt ?? 1);
  const rootZone = Number(values?.root_zone_show_cnt ?? 1);

  if (isSimulation) {
    const sharedCount = Math.max(rootZone, irrigation);
    return { irrigation: sharedCount, rootZone: sharedCount };
  }

  return {
    irrigation,
    rootZone: supportsGrowBaseProRootZoneCount(controller) ? rootZone : 1
  };
}

export function hasIrrigationError(controller, value) {
  const numericValue = Number(value);
  if (!Number.isFinite(numericValue) || numericValue < 0) {
    return false;
  }

  return Number(controller?.type) === GROWBASE_PRO_CONTROLLER_TYPE
    ? numericValue >= 5
    : numericValue !== 0;
}

export function normalizeP1MinimumDurationMinutes(value) {
  const numericValue = Number(value);
  return Number.isFinite(numericValue) ? Math.min(20, Math.max(1, numericValue)) : 5;
}

// API BAIB changes only shot timeouts; phase and wait settings remain minutes.
export function supportsIrrigationShotTimeoutSeconds(controller) {
  return getControllerApiVersion(controller).localeCompare('BAIB') >= 0;
}

export const IRRIGATION_ADAPTIVE_FIELDS = [
  'irrigation_p2_adaptive_enabled',
  'irrigation_p2_learning_enabled',
  'irrigation_p2_refill_soak_seconds',
  'irrigation_p2_max_total_watering_seconds'
];

// GUI editing ranges; controller readback and wire representation remain unchanged.
export const IRRIGATION_P2_SOAK_MIN_SECONDS = 30;
export const IRRIGATION_P2_SOAK_MAX_SECONDS = 10 * 60;
export const IRRIGATION_P2_TOTAL_MAX_SECONDS = 60 * 60;

export function supportsIrrigationReferenceInterval(controller) {
  return [GROWBASE_PRO_CONTROLLER_TYPE, INDUSTRIAL_LITE_CONTROLLER_TYPE, 6000].includes(Number(controller?.type)) &&
    getControllerApiVersion(controller).localeCompare('BAICA') >= 0;
}

export function supportsAdaptiveIrrigation(controller, irrigation) {
  const model = Number(controller?.type);
  return [GROWBASE_PRO_CONTROLLER_TYPE, INDUSTRIAL_LITE_CONTROLLER_TYPE].includes(model) &&
    getControllerApiVersion(controller).localeCompare('BAICA') >= 0 &&
    IRRIGATION_ADAPTIVE_FIELDS.every((key) =>
      Object.prototype.hasOwnProperty.call(irrigation ?? {}, key));
}

// The ordinary and scheduled editors share the installed safety-derived slider limits.
export function irrigationP1ShotTimeoutMinimum(controller, irrigation, adaptiveSupported) {
  const seconds = supportsIrrigationShotTimeoutSeconds(controller);
  const minimum = seconds ? 10 : 1;
  if (adaptiveSupported || !isIrrigationAutoStrategy(irrigation?.irrigation_strategy)) return minimum;
  const noIncrease = Number(irrigation?.irrigation_P1_vwc_auto_sp_max_duration_no_increase_seconds) || 0;
  return Math.max(minimum, seconds ? noIncrease : Math.ceil(noIncrease / 60));
}

export function irrigationWaitMinimumMinutes(controller, irrigation, phase, adaptiveSupported) {
  const seconds = supportsIrrigationShotTimeoutSeconds(controller);
  if (phase === 2 && adaptiveSupported && irrigation.irrigation_p2_adaptive_enabled === 1)
    return Math.max(1, Math.ceil(irrigation.irrigation_p2_refill_soak_seconds / 60));
  const timeout = Math.max(
    seconds ? 10 : 1,
    Number(irrigation?.[irrigationShotTimeoutKey(phase, seconds)]) || 0,
    phase === 1 ? irrigationP1ShotTimeoutMinimum(controller, irrigation, adaptiveSupported) : 0
  );
  return seconds ? Math.ceil(timeout / 60) : timeout;
}

export function normalizeIrrigationAdaptiveDurations(settings, controller) {
  return {
    ...settings,
    irrigation_settings: settings.irrigation_settings.map((irrigation) => {
      if (!supportsAdaptiveIrrigation(controller, irrigation) ||
          irrigation.irrigation_p2_adaptive_enabled !== 1 ||
          !isIrrigationAdvancedVwcStrategy(irrigation.irrigation_strategy)) return irrigation;
      const total = Math.max(10, Math.min(IRRIGATION_P2_TOTAL_MAX_SECONDS,
        irrigation.irrigation_p2_max_total_watering_seconds));
      const soak = Math.max(IRRIGATION_P2_SOAK_MIN_SECONDS,
        Math.min(IRRIGATION_P2_SOAK_MAX_SECONDS, irrigation.irrigation_p2_refill_soak_seconds));
      return {
        ...irrigation,
        irrigation_p2_refill_soak_seconds: soak,
        irrigation_p2_wait_after_shot_s: Math.max(irrigation.irrigation_p2_wait_after_shot_s,
          Math.ceil(soak / 60)),
        irrigation_p2_max_total_watering_seconds: total,
        irrigation_p2_shot_duration_timeout_seconds: Math.max(10,
          Math.min(total, irrigation.irrigation_p2_shot_duration_timeout_seconds))
      };
    })
  };
}

export function irrigationShotTimeoutKey(phase, seconds) {
  return `irrigation_p${phase}_shot_duration_timeout_${seconds ? 'seconds' : 'minutes'}`;
}

// Convert explicitly named legacy fields on import, before merging with readback.
// A seconds draft on an older controller remains incompatible, never rounded.
export function importIrrigationTimeouts(settings, controller) {
  const result = _.cloneDeep(settings);
  if (!supportsIrrigationShotTimeoutSeconds(controller)) return result;
  for (const phase of [1, 2]) {
    const minutes = irrigationShotTimeoutKey(phase, false);
    const seconds = irrigationShotTimeoutKey(phase, true);
    if (Object.prototype.hasOwnProperty.call(result, minutes)) {
      if (typeof result[minutes] !== 'number' || !Number.isInteger(result[minutes])) return null;
      if (Object.prototype.hasOwnProperty.call(result, seconds) && result[seconds] !== result[minutes] * 60)
        return null;
      result[seconds] = result[minutes] * 60;
      delete result[minutes];
    }
  }
  return result;
}

export function validIrrigationTimeouts(settings, controller) {
  const seconds = supportsIrrigationShotTimeoutSeconds(controller);
  return settings.irrigation_settings?.every((irrigation) => {
    for (const phase of [1, 2, 3]) {
      const key = irrigationShotTimeoutKey(phase, seconds);
      if (Object.prototype.hasOwnProperty.call(irrigation, irrigationShotTimeoutKey(phase, !seconds)))
        return false;
      if (!seconds && phase === 3) continue;
      const value = irrigation[key];
      if (!Number.isInteger(value) || value < (seconds ? 10 : 1) ||
          value > (phase === 1 ? 20 : 60) * (seconds ? 60 : 1)) return false;
    }
    return true;
  }) ?? false;
}

export function irrigationEndTimeForWire(value) {
  if (typeof value !== 'number') return value;
  const hours = Math.floor(value / 60) % 24;
  const minutes = value % 60;
  return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}:00`;
}

const irrigationDraftRows = (rows) => Object.fromEntries(
  Object.entries(rows).filter(([, row]) => row != null)
);

// Adding/removing events saves complete rows; editing a field saves only that
// indexed field. Empty lists are intentional replacements (delete all events).
const isCompleteIrrigationEventList = (rows) => Array.isArray(rows) && Array.from(rows).every(
  (row) => row != null && [
    'irrigation_index', 'phase', 'enabled', 'start_minute_from_phase_begin', 'duration_seconds'
  ].every((key) => Object.prototype.hasOwnProperty.call(row, key))
);

export function restoreIrrigationDraft(baseline, draft) {
  const restoredDraft = _.cloneDeep(draft);
  // JSON turns sparse array holes from edits to later channels into nulls.
  if (restoredDraft.irrigation_settings) {
    restoredDraft.irrigation_settings = irrigationDraftRows(restoredDraft.irrigation_settings);
  }
  if (restoredDraft.irrigation_events && !isCompleteIrrigationEventList(restoredDraft.irrigation_events)) {
    restoredDraft.irrigation_events = irrigationDraftRows(restoredDraft.irrigation_events);
  }
  return restoreSettingsDraft(baseline, restoredDraft, (current, saved, key) =>
    key === 'irrigation_events' && Array.isArray(saved) ? _.cloneDeep(saved) : undefined
  );
}

// Reconcile only submitted edits with authoritative readback. Later edits and
// rejected fields stay in the existing nested draft store.
export function reconcileIrrigationDraft(draft, submitted, readback) {
  const remaining = {};
  for (const [key, value] of Object.entries(draft)) {
    if (key === '_deleted_indicies' && _.isEmpty(value)) continue;
    const wireValue = key === 'irrigation_p2_end_before_sunset'
      ? irrigationEndTimeForWire(value) : value;
    if (_.isEqual(wireValue, submitted?.[key]) && _.isEqual(wireValue, readback?.[key])) continue;
    if (_.isPlainObject(value)) {
      const child = reconcileIrrigationDraft(value, submitted?.[key], readback?.[key]);
      if (!_.isEmpty(child)) remaining[key] = child;
    } else if (Array.isArray(value) && (key === 'irrigation_settings' ||
        (key === 'irrigation_events' && !isCompleteIrrigationEventList(value)))) {
      const child = reconcileIrrigationDraft(irrigationDraftRows(value), submitted?.[key], readback?.[key]);
      if (!_.isEmpty(child)) remaining[key] = child;
    } else remaining[key] = _.cloneDeep(value);
  }
  return remaining;
}
